import Cookie from 'js-cookie';
import { getCookie } from '@/lib/cookie-helpers';
import {
  QueryParamNames,
  TrialDurationOverrides,
} from '@/constants/QueryParams';
import CookieNames from '@/constants/CookieNames';

/* eslint-disable no-else-return */
import { computed, ComputedRef } from '@nuxtjs/composition-api';
import { addDays } from 'date-fns';
import useRegistration, {
  RegistrationStateNames,
} from '@/modules/SelfServiceRegistration/hooks/useRegistration';

export const trialPrice = 1; // in dollars
const shortTrialDuration = 7; // in days
const longTrialDuration = 30; // in days
const longestTrialDuration = 90; // in days

// Tiers of the six-band ladder (BIO-2209): 0–19, 20–49 and 50–99 get 7 days; 100–199, 200–299
// and 300–500 get 30. Anything else falls through to the 7-day default.
const tiersWithShortTrial = [1, 2, 3];
const tiersWithLongTrial = [4, 5, 6];
const tiersWithLongestTrial = [];

// The lower bound of each band, smallest first.
const BAND_FLOORS = [1, 20, 50, 100, 200, 300];

/**
 * The ladder tier a company-size answer falls in, by the low end of its range: the band answers
 * ('100-199') land on their own tier, and an older Dedicated range ('71-149') on the band of its
 * low end, the rule bandPlanFor uses. Null for no answer or a non-employer ('0-0').
 */
export const tierForCompanySize = (size?: string | null): number | null => {
  const min = Number(String(size || '').split('-')[0]);
  if (!min) {
    return null;
  }

  return BAND_FLOORS.filter((floor) => min >= floor).length || null;
};

const hasTierInTrialList = (
  tierList: number[],
  tierNumber: number | null
): boolean => {
  if (tierNumber === null) {
    return false;
  }

  return tierList.includes(tierNumber);
};

const jpMorgan = 'jp-morgan';

const useTrials = () => {
  const { state: registrationState } = useRegistration();

  // Registration never sets TIER_NUMBER today, so the tier comes from the size answer (BIO-2209).
  const selectedTierNumber = computed(
    () =>
      (registrationState[RegistrationStateNames.TIER_NUMBER] as unknown as
        | number
        | null) ??
      tierForCompanySize(
        registrationState[RegistrationStateNames.NUM_EMPLOYEES] as unknown as
          | string
          | null
      )
  );

  const hasShortTrial: ComputedRef<boolean> = computed(() =>
    hasTierInTrialList(tiersWithShortTrial, selectedTierNumber.value)
  );

  const hasLongTrial: ComputedRef<boolean> = computed(() =>
    hasTierInTrialList(tiersWithLongTrial, selectedTierNumber.value)
  );

  const hasLongestTrial: ComputedRef<boolean> = computed(
    () =>
      hasTierInTrialList(tiersWithLongestTrial, selectedTierNumber.value) ||
      getCookie('referredByCode') === jpMorgan
  );

  const trialDuration: ComputedRef<number> = computed(() => {
    // If query params, look for the trial duration cookie
    const cookieQuery = Cookie.get(CookieNames.QUERY_PARAMS);
    if (cookieQuery) {
      const cookieQueryStr = cookieQuery || '{}';
      const cookieQueryObj = JSON.parse(cookieQueryStr);
      const trialCookie =
        cookieQueryObj[QueryParamNames.TRIAL_DURATION_OVERRIDE] || '';
      switch (trialCookie) {
        case TrialDurationOverrides.NINETY:
          return 90;
        case TrialDurationOverrides.SIXTY:
          return 60;
        case TrialDurationOverrides.THIRTY:
          return 30;
        case TrialDurationOverrides.FIFTEEN:
          return 15;
        case TrialDurationOverrides.SEVEN:
          return 7;
        default:
          break;
      }
    }

    if (hasLongestTrial.value) {
      return longestTrialDuration;
    }

    if (hasShortTrial.value) {
      return shortTrialDuration;
    }

    if (hasLongTrial.value) {
      return longTrialDuration;
    }

    return shortTrialDuration;
  });

  const trialEndDate: ComputedRef<Date> = computed(() => {
    const today = new Date();
    const endDate = addDays(today, trialDuration.value || 0);

    return endDate;
  });

  return {
    trialDuration,
    trialEndDate,
  };
};

export default useTrials;
